import { ReactNode, createContext, useContext, useState } from 'react';
import { Button } from 'src/components/Button';
import { Heading } from 'src/components/Heading';
import { useCrmSync } from '../../contexts/CrmSyncContext';
import { useCampaignContext } from '../../contexts/CampaignContext';

interface ExtraStepConfigurationContextValue {
  isConfigurationComplete: boolean;
  setIsConfigurationComplete: (complete: boolean) => void;
}

const ExtraStepConfigurationContext = createContext<
  ExtraStepConfigurationContextValue | undefined
>(undefined);

export const useExtraStepConfiguration = () => {
  const context = useContext(ExtraStepConfigurationContext);
  if (context === undefined) {
    throw new Error(
      'useExtraStepConfiguration must be used within an ExtraStepConfiguration',
    );
  }
  return context;
};

interface ExtraStepConfigurationProps {
  children?: ReactNode;
}

export const ExtraStepConfiguration = ({
  children,
}: ExtraStepConfigurationProps) => {
  const { syncInProgress, navigateToReturnTo, handleConfigurationComplete } =
    useCrmSync();
  const { isInitializing } = useCampaignContext();

  const [isConfigurationComplete, setIsConfigurationComplete] = useState(false);

  const disabled = syncInProgress || isInitializing || !isConfigurationComplete;
  const onContinue = handleConfigurationComplete;
  const onBack = () => navigateToReturnTo();

  const contextValue: ExtraStepConfigurationContextValue = {
    isConfigurationComplete,
    setIsConfigurationComplete,
  };

  return (
    <ExtraStepConfigurationContext.Provider value={contextValue}>
      <Heading
        title="Configure Integration"
        subtitle="Configure your CRM integration settings"
      >
        <div className="flex items-center gap-3">
          <Button variant="outlined" onClick={onBack}>
            Back
          </Button>
          <Button variant="contained" onClick={onContinue} disabled={disabled}>
            Continue to Field Mapping
          </Button>
        </div>
      </Heading>

      <div className="max-w-4xl w-full mx-auto">
        <div className="space-y-8">{children}</div>
      </div>
    </ExtraStepConfigurationContext.Provider>
  );
};
